"use client";

import ButtonWithIcon from "@/components/atoms/button-with-icon";
import { Button, buttonVariants } from "@/components/atoms/buttons";
import { Graphics } from "@/components/graphics";
import { sanitizeImageUrl } from "@/components/molecules/image-gallery";
import ProductRange from "@/components/organisms/product-range";
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { isProductCustomizationComplete } from "@/lib/product-customizations";
import { cn, extractKeyTextValue } from "@/lib/utils";
import { ProductDocument } from "@/prismicio-types";
import { useCheckoutStore } from "@/stores/checkout-store";
import { StoreProduct } from "@medusajs/types";
import { PrismicRichText } from "@prismicio/react";
import Image from "next/image";
import Link from "next/link";
import AutoHeight from "embla-carousel-auto-height";
import { useEffect, useState } from "react";
import { useAddToCart, useRemoveCartItem } from "@/hooks/useCartMutations";
import { useCart } from "@/hooks/useCart";
import { Loader2 } from "lucide-react";

type ProductConfigurationModalProps = {
  triggerClassName?: string;
  showTriggerIcon?: boolean;
  locale: Locales;
  products: ProductDocument[];
  medusaProducts: Record<string, StoreProduct> | undefined;
  onlyIcon?: boolean;
  addProductToCart?: boolean;
  isInWishlist?: boolean;
  activeListId?: string | null;
  // When set, the store is seeded with this surface every time the dialog
  // opens. Used by PaymentProductCard / CartProductItem so the modal always
  // shows the variant actually in the cart — not a stale store value from a
  // previous PDP visit where the user changed surface without adding to cart.
  defaultSurface?: number;
  hideWhenConfigured?: boolean;
};

const ProductConfigurationModal = ({
  triggerClassName,
  showTriggerIcon = true,
  locale,
  products,
  medusaProducts,
  onlyIcon = false,
  addProductToCart = true,
  isInWishlist = false,
  activeListId,
  defaultSurface,
  hideWhenConfigured = false,
}: ProductConfigurationModalProps) => {
  const [open, setOpen] = useState(false);
  const [api, setApi] = useState<CarouselApi>();
  const [currentSlide, setCurrentSlide] = useState(0);

  // ── Store ─────────────────────────────────────────────────────────────────
  // productCustomizations is reactive — changes made by ProductRange inside
  // the modal (which writes to the store) are reflected here immediately.
  const productCustomizations = useCheckoutStore((state) => state.productCustomizations);
  const setProductCustomization = useCheckoutStore((state) => state.setProductCustomization);

  // ── Current slide's product identity ─────────────────────────────────────
  const currentProduct = products[currentSlide];
  const currentMedusaId = currentProduct ? extractKeyTextValue(currentProduct.data.medusaId) : undefined;
  const currentMedusaProduct = currentMedusaId ? medusaProducts?.[currentMedusaId] : undefined;

  // ── Resolved surface for the current slide ────────────────────────────────
  // Priority: store (written by ProductRange) → product surface_min
  // We intentionally do NOT read the URL here — the modal is used on pages
  // other than the PDP where ?surface= is meaningless.
  const resolvedSurfaceForCurrentSlide: number | undefined =
    (currentMedusaId ? productCustomizations[currentMedusaId]?.surface : undefined) ??
    (currentMedusaProduct?.metadata?.surface_min as number | undefined);

  const productConfigurationStates = products.map((product) => {
    const medusaId = extractKeyTextValue(product.data.medusaId);
    const medusaProduct = medusaProducts?.[medusaId];

    return isProductCustomizationComplete(medusaProduct, productCustomizations[medusaId]);
  });
  const isConfigured = productConfigurationStates.some(Boolean);
  const isFullyConfigured = productConfigurationStates.length > 0 && productConfigurationStates.every(Boolean);
  const totalProducts = products.length;

  const { cart } = useCart();
  const { addToCart, isLoading: isAddingToCart } = useAddToCart({
    onSuccess: () => {
      // Persist the surface that was just used to add to cart, so other
      // pages (wishlist card, "add all") can read the same value from the store.
      if (currentMedusaId && resolvedSurfaceForCurrentSlide) {
        setProductCustomization(currentMedusaId, { surface: resolvedSurfaceForCurrentSlide });
      }
      setTimeout(() => setOpen(false), 500);
    },
  });
  const { removeItem } = useRemoveCartItem();

  useEffect(() => {
    if (!api) return;
    setCurrentSlide(api.selectedScrollSnap());
    const onSelect = () => setCurrentSlide(api.selectedScrollSnap());
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  const goToPrevSlide = () => api?.scrollPrev();
  const goToNextSlide = () => api?.scrollNext();

  if (hideWhenConfigured && isFullyConfigured) {
    return null;
  }

  // ── Seed store on open when defaultSurface is provided ───────────────────
  // Ensures the cart-page modal always opens showing the variant actually in
  // the cart — not a stale value left in the store from a previous PDP visit.
  // On every open we overwrite with the cart's actual surface so even if the
  // user changed and closed without saving, the next open is always correct.
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen && defaultSurface !== undefined && currentMedusaId) {
      setProductCustomization(currentMedusaId, { surface: defaultSurface });
    }
    setOpen(newOpen);
  };

  const handleClick = () => {
    if (!currentMedusaId || !currentMedusaProduct) return;

    // Single-variant product: nothing to select, just close
    if (!currentMedusaProduct.variants || currentMedusaProduct.variants.length <= 1) {
      setOpen(false);
      return;
    }

    // No surface resolved (shouldn't happen for a properly configured product,
    // but guard defensively instead of silently bailing)
    if (!resolvedSurfaceForCurrentSlide) {
      console.warn("No surface resolved for product", currentMedusaId);
      setOpen(false);
      return;
    }

    // Store-only save — no cart mutation
    if (!addProductToCart) {
      setOpen(false);
      return;
    }

    const selectedVariant = currentMedusaProduct.variants.find(
      (v) => v.metadata?.["surface"] === resolvedSurfaceForCurrentSlide
    );

    if (!selectedVariant) {
      console.warn("No variant found for surface", resolvedSurfaceForCurrentSlide);
      return;
    }

    if (isInWishlist) {
      // Wishlist context: swap variant in cart for this wishlist
      const existingSelectedItem = cart?.items?.find((i) => i.variant_id === selectedVariant.id);
      if (existingSelectedItem) {
        // Already the correct variant in cart — nothing to do
        setOpen(false);
        return;
      }

      const existingOtherItem = cart?.items?.find((item) =>
        currentMedusaProduct.variants?.some((v) => v.id === item.variant_id)
      );
      const quantity = existingOtherItem?.quantity ?? 1;

      if (existingOtherItem) removeItem(existingOtherItem.id);

      addToCart(selectedVariant.id, quantity, {
        wishlist_ids: activeListId ? [activeListId] : [],
      });
      return;
    }

    // PDP / non-wishlist context: replace existing variant
    const existingCartItemForProduct = cart?.items?.find((item) =>
      currentMedusaProduct.variants?.some((v) => v.id === item.variant_id)
    );
    const quantity = existingCartItemForProduct?.quantity ?? 1;
    if (existingCartItemForProduct) removeItem(existingCartItemForProduct.id);
    addToCart(selectedVariant.id, quantity);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button
          className={cn(
            "bg-pink-primary focus-visible:ring-pink-primary/50 line-clamp-1 flex min-h-9 cursor-pointer items-center gap-1.5 rounded-sm p-2 text-start text-white transition-all duration-300 ease-in-out outline-none focus-visible:ring-[3px] focus-visible:ring-offset-1",
            triggerClassName
          )}
        >
          {showTriggerIcon && <Graphics.configureIcon />}
          {!onlyIcon && (
            <p className="text-sm font-medium">
              {isConfigured && products.length === 1
                ? __("Update your configuration", locale)
                : totalProducts === 1
                  ? __("Configure item to place an order.", locale)
                  : __("Configure items to place an order.", locale)}
            </p>
          )}
        </button>
      </DialogTrigger>

      <DialogContent
        className={cn(
          "text-dark-primary mb-0 max-h-[90vh] max-w-[calc(100%-2rem)] gap-0 rounded-[10px] px-5 py-4 sm:max-w-[560px] sm:py-7"
        )}
      >
        <DialogHeader>
          <DialogTitle className="text-1xl sr-only text-start font-medium">
            {__("Product Configuration", locale)}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {__("This is a Dialog box modal for configuring the product.", locale)}
          </DialogDescription>
        </DialogHeader>

        <Carousel setApi={setApi} plugins={[AutoHeight()]} className="relative w-full overflow-hidden rounded-t-md">
          <CarouselContent className="flex items-start">
            {products.map((product, index) => {
              const medusaId = extractKeyTextValue(product.data.medusaId);
              const medusaProduct = medusaProducts?.[medusaId];
              if (!medusaProduct) return null;

              return (
                <CarouselItem key={index} className="relative basis-full">
                  <ScrollArea className="h-[85vh] pb-32">
                    <figure className="bg-product-card relative mb-3.5 flex h-[267px] items-center justify-center overflow-hidden rounded-md">
                      {product.data.image_full_size_card ? (
                        <Image
                          src={
                            sanitizeImageUrl(medusaProduct?.images?.at(0)?.url || "") ||
                            product.data.images[0]?.image.url ||
                            product.data.featuredImage.url ||
                            ""
                          }
                          alt={`Product Image ${index + 1}`}
                          fill
                          className="absolute object-cover mix-blend-multiply"
                        />
                      ) : (
                        <Image
                          src={
                            sanitizeImageUrl(medusaProduct?.images?.at(0)?.url || "") ||
                            product.data.images[0]?.image.url ||
                            product.data.featuredImage.url ||
                            ""
                          }
                          alt={`Product Image ${index + 1}`}
                          width={224}
                          height={224}
                          className="object-contain mix-blend-multiply"
                        />
                      )}
                    </figure>

                    <section className="mb-3.5 space-y-3.5">
                      <h2 className="font-cabinet-grotesk leading-h1 text-2xl font-medium md:text-3xl">
                        {product.data.title}
                      </h2>
                      <PrismicRichText
                        field={product.data?.description}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="text-primary-light leading-body-small text-sm">{children}</p>
                          ),
                        }}
                      />
                    </section>

                    {/* ProductRange writes to store via setVariant — no URL manipulation */}
                    <ProductRange
                      medusaProduct={medusaProduct}
                      className="py-3.5"
                      locale={locale}
                      medusaId={medusaProduct.id}
                      seedFromUrl={false}
                    />
                  </ScrollArea>

                  <div className="border-primary-light/50 absolute bottom-0 z-20 w-full space-y-3.5 border-t bg-white pt-3.5 pr-5">
                    <Link
                      href={`${getPermalink(locale, "product")}/${product.uid}`}
                      className={cn(
                        buttonVariants({ variant: "secondary", size: "md" }),
                        "border-primary-light h-11 w-full"
                      )}
                    >
                      {__("View Product", locale)}
                    </Link>
                    <Button size="md" className="h-11 w-full" onClick={handleClick} disabled={isAddingToCart}>
                      {isAddingToCart ? (
                        <Loader2 className="animate-spin" />
                      ) : (
                        <>
                          {__("Save my configuration", locale)}{" "}
                          {totalProducts > 1 && `(${currentSlide + 1}/${totalProducts})`}
                        </>
                      )}
                    </Button>
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>

        {totalProducts > 1 && (
          <>
            <ButtonWithIcon
              iconLeftName="ArrowLeft"
              variant="light"
              className="bg-pink-primary hover:pink-overlay absolute top-[48%] right-0 size-11 rounded-none text-white disabled:opacity-50 sm:top-[50%] sm:-left-[8%]"
              aria-label="Go to previous product"
              onClick={goToPrevSlide}
              disabled={currentSlide === 0}
            >
              <span className="sr-only">Previous slide</span>
            </ButtonWithIcon>
            <ButtonWithIcon
              iconLeftName="ArrowRight"
              variant="light"
              className="bg-pink-primary hover:pink-overlay absolute top-[55%] right-0 size-11 rounded-none text-white disabled:opacity-50 sm:top-[50%] sm:-right-[8%]"
              aria-label="Go to next product"
              onClick={goToNextSlide}
              disabled={currentSlide === totalProducts - 1}
            >
              <span className="sr-only">Next slide</span>
            </ButtonWithIcon>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProductConfigurationModal;
